<template>
  <div class="acuity-management">
    <div class="container-fluid">
      <div class="header-section">
        <h1>Acuity Level Management</h1>
        <div class="action-buttons">
          <button 
            v-if="$canAccess('PatientActivityLevelAdd')"
            class="btn btn-primary" 
            @click="$router.push({ name: 'PatientActivityLevelAdd' })"
          >
            <i class="fas fa-plus-circle"></i> Add Activity Level
          </button>
          <button
            class="btn btn-secondary ms-2"
            @click="$router.push({ name: 'nurses' })"
          >
            <i class="fas fa-arrow-left"></i> Back to Nurses
          </button>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Filters Section -->
      <div class="filters-section mb-4">
        <div class="card">
          <div class="card-header bg-light">
            <h5 class="mb-0">Search</h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-12">
                <div class="d-flex gap-2">
                  <div class="d-flex gap-2 w-100">
                    <input
                      type="text"
                      class="form-control form-element"
                      style="max-width: 300px;"
                      v-model="searchTerm"
                      placeholder="Search by name or code..."
                    >
                    <select
                      class="form-select form-element"
                      style="max-width: 200px;"
                      v-model="levelFilter"
                    >
                      <option value="all">All Levels</option>
                      <option value="1">Level 1</option>
                      <option value="2">Level 2</option>
                      <option value="3">Level 3</option>
                      <option value="4">Level 4</option>
                      <option value="5">Level 5</option>
                    </select>
                    <button class="btn btn-secondary form-element" @click="resetFilters">Reset</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Acuity Levels Table -->
      <div v-if="!loading && acuityLevels && acuityLevels.length > 0" class="acuity-table-container">
        <table class="table table-striped table-hover table-bordered text-start w-100">
          <thead>
            <tr>
              <th @click="sortBy('name')">
                Name 
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('code')">
                Code
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('level')">
                Level
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('nurse_patient_ratio')">
                Nurse-Patient Ratio
                <i class="fas fa-sort"></i>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="acuity in paginatedAcuityLevels" :key="acuity.id">
              <td>{{ acuity.name }}</td>
              <td>{{ acuity.code }}</td>
              <td>
                <span class="badge" :class="getLevelBadgeClass(acuity.level)">
                  Level {{ acuity.level }}
                </span>
              </td>
              <td>1:{{ acuity.nurse_patient_ratio }}</td>
              <td>
                <div class="btn-group">
                  <button 
                    v-if="$canAccess('PatientActivityLevelEdit')"
                    class="btn btn-sm btn-outline-primary" 
                    @click="editAcuityLevel(acuity.id)"
                  >
                    <i class="fas fa-edit"></i> Edit
                  </button>
                  <button 
                    class="btn btn-sm btn-outline-info" 
                    @click="viewAcuityLevelDetails(acuity.id)"
                  >
                    <i class="fas fa-eye"></i> Details
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination-container">
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="setPage(currentPage - 1)">Previous</button>
              </li>
              <li 
                v-for="page in totalPages" 
                :key="page" 
                class="page-item"
                :class="{ active: page === currentPage }"
              >
                <button class="page-link" @click="setPage(page)">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="setPage(currentPage + 1)">Next</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && (!acuityLevels || acuityLevels.length === 0)" class="empty-state">
        <div class="empty-state-content">
          <i class="fas fa-chart-line fa-3x"></i>
          <h3>No Acuity Levels Found</h3>
          <p>There are no acuity levels in the system that match your filters, or none have been defined yet.</p>
          <button 
            v-if="$canAccess('PatientActivityLevelAdd')"
            class="btn btn-primary" 
            @click="$router.push({ name: 'PatientActivityLevelAdd' })"
          >
            <i class="fas fa-plus-circle"></i> Add Acuity Level
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PatientActivityLevelList',
  data() {
    return {
      acuityLevels: [],
      loading: false,
      error: null,
      searchTerm: '',
      levelFilter: 'all',
      sortColumn: 'level',
      sortDirection: 'asc',
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    filteredAcuityLevels() {
      if (!this.acuityLevels) return [];

      let filtered = [...this.acuityLevels];
      
      // Apply search filter
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        filtered = filtered.filter(acuity => 
          acuity.name.toLowerCase().includes(searchLower) ||
          acuity.code.toLowerCase().includes(searchLower) ||
          acuity.description?.toLowerCase().includes(searchLower)
        );
      }
      
      // Apply level filter
      if (this.levelFilter !== 'all') {
        filtered = filtered.filter(acuity => 
          acuity.level === parseInt(this.levelFilter)
        );
      }
      
      // Apply sorting
      filtered.sort((a, b) => {
        let valueA = a[this.sortColumn];
        let valueB = b[this.sortColumn];
        
        // Handle null values
        if (valueA === null) valueA = '';
        if (valueB === null) valueB = '';
        
        // String comparison
        if (typeof valueA === 'string') {
          valueA = valueA.toLowerCase();
          valueB = valueB.toLowerCase();
        }
        
        // Do the actual comparison
        if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      return filtered;
    },
    paginatedAcuityLevels() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.filteredAcuityLevels.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.filteredAcuityLevels.length / this.itemsPerPage);
    }
  },
  methods: {
    async fetchAcuityLevels() {
      this.loading = true;
      this.error = null;

      try {
        const response = await this.$api.get('/patient-activity-levels');
        this.acuityLevels = response.data.data;
      } catch (error) {
        console.error('Error fetching activity levels:', error);
        this.error = 'Failed to load activity levels. Please try again later.';
        this.acuityLevels = [];
      } finally {
        this.loading = false;
      }
    },
    
    getLevelBadgeClass(level) {
      switch (level) {
        case 1: return 'bg-success';
        case 2: return 'bg-info';
        case 3: return 'bg-warning';
        case 4: return 'bg-danger';
        case 5: return 'bg-dark';
        default: return 'bg-secondary';
      }
    },
    
    sortBy(column) {
      if (this.sortColumn === column) {
        // Toggle direction if already sorting by this column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Default to ascending for new column
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    
    setPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    
    resetFilters() {
      this.searchTerm = '';
      this.levelFilter = 'all';
      this.currentPage = 1;
    },
    
    editAcuityLevel(acuityId) {
      this.$router.push({ 
        name: 'PatientActivityLevelEdit',
        params: { id: acuityId } 
      });
    },
    
    viewAcuityLevelDetails(acuityId) {
      this.$router.push({ 
        name: 'PatientActivityLevelView', 
        params: { id: acuityId } 
      });
    }
  },
  mounted() {
    this.fetchAcuityLevels();
  }
}
</script>

<style scoped>
.acuity-management {
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
}

.empty-state-content {
  max-width: 400px;
  margin: 0 auto;
}

.empty-state i {
  color: #6c757d;
  margin-bottom: 15px;
}

.empty-state h3 {
  margin-bottom: 10px;
}

.empty-state p {
  margin-bottom: 20px;
  color: #6c757d;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 50px 0;
}

.badge {
  font-size: 0.85rem;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.filters-section {
  transition: all 0.3s ease;
}

.form-element {
  height: 38px;
  box-sizing: border-box;
}

.form-control, .form-select, .btn {
  line-height: normal;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .action-buttons {
    display: flex;
    width: 100%;
    gap: 10px;
  }
}
</style>
